import { useEffect, useRef, useState } from "react";
import { discoverClient } from "../discoverClient";
import { normalizeDiscoveryCatalogue } from "./discoveryCatalogue";
import type { DiscoveryCardModel } from "./types";

/** Exploration only offers apps published in the last month. */
const EXPLORATION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * For You adds one fresh app the person has not seen, at most one request.
 *
 * The read is separate from the paged list so it cannot move the cursor, and
 * every failure simply renders no card. The candidate must be new, not
 * already displayed and not hidden.
 */
export function useDiscoverExploration({
  enabled,
  displayedIds,
  hiddenIds,
}: {
  enabled: boolean;
  displayedIds: ReadonlySet<string>;
  hiddenIds: ReadonlySet<string>;
}): DiscoveryCardModel | null {
  const [card, setCard] = useState<DiscoveryCardModel | null>(null);
  const attempted = useRef(false);
  // Read at resolution time: the first page may answer after this effect.
  const displayedRef = useRef(displayedIds);
  displayedRef.current = displayedIds;
  const hiddenRef = useRef(hiddenIds);
  hiddenRef.current = hiddenIds;
  useEffect(() => {
    if (!enabled || attempted.current) return;
    attempted.current = true;
    let cancelled = false;
    discoverClient
      .page({ sort: "new", limit: 3 })
      .then((page) => {
        if (cancelled) return;
        const now = Date.now();
        const apps = Array.isArray(page?.apps) ? page.apps : [];
        const candidate = apps.find((app) => {
          if (!app || typeof app.id !== "string") return false;
          if (displayedRef.current.has(app.id)) return false;
          if (hiddenRef.current.has(app.id)) return false;
          const published = app.published_at ? Date.parse(app.published_at) : NaN;
          if (!Number.isFinite(published) || published > now) return false;
          return now - published <= EXPLORATION_MAX_AGE_MS;
        });
        const entry = candidate
          ? normalizeDiscoveryCatalogue([candidate], [])[0] ?? null
          : null;
        setCard(entry);
      })
      .catch(() => {
        /* Exploration is optional; the page renders without it. */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return enabled ? card : null;
}
